import { useEffect, useState } from 'react';
import { AuthService } from '../../services/auth.service';
import { BankAccountService } from '../../services/bankAccount.service';
import { AccountService } from '../../services/account.service';
import { SegmentService } from '../../services/segment.service';
import { BankAccount } from '../../interfaces/bank/bankAccount.interface';
import { Account, Segment } from '../../interfaces/accounting.interface';
import { leafAccounts } from '../../utils/postingAccounts';

const asList = <T,>(r: unknown): T[] => (Array.isArray(r) ? r : ((r as { content?: T[] } | undefined)?.content ?? []));

export interface TreasuryRefData {
  banks: BankAccount[];
  assets: Account[];
  expenses: Account[];
  revenues: Account[];
  segments: Segment[];
  loaded: boolean;
}

/** Reference lists the treasury pages share: bank accounts, posting accounts by type, segments. */
export function useTreasuryRefData(onError?: (e: unknown) => void): TreasuryRefData {
  const institutionId = AuthService.getSelectedInstitution()!;
  const [data, setData] = useState<TreasuryRefData>({ banks: [], assets: [], expenses: [], revenues: [], segments: [], loaded: false });
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [banks, a, x, r, seg] = await Promise.all([
          BankAccountService.listByInstitution(institutionId),
          AccountService.getAccountsByType('ASSET'),
          AccountService.getAccountsByType('EXPENSE'),
          AccountService.getAccountsByType('REVENUE'),
          SegmentService.getByInstitution(0, 200),
        ]);
        if (!alive) return;
        setData({
          banks: banks ?? [],
          assets: leafAccounts(asList<Account>(a)),
          expenses: leafAccounts(asList<Account>(x)),
          revenues: leafAccounts(asList<Account>(r)),
          segments: asList<Segment>(seg).filter(s => s.active !== false),
          loaded: true,
        });
      } catch (e) {
        if (alive) { onError?.(e); setData(d => ({ ...d, loaded: true })); }
      }
    })();
    return () => { alive = false; };
  }, [institutionId]); // eslint-disable-line react-hooks/exhaustive-deps
  return data;
}

export const bankLabel = (b: BankAccount): string => `${b.bankName} ${b.accountNumber}${b.currencyCode ? ` (${b.currencyCode})` : ''}`;
export const segmentLabel = (s: Segment): string => `${s.code} - ${s.name}`;
export const FILTER_CELL = { flex: '1 1 220px', maxWidth: 320 } as const;
